import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';

interface VideoCardProps {
  video: VideoItem;
  variant?: 'default' | 'compact';
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  const formatViews = (views: number) => {
    if (views >= 10000) {
      return (views / 10000).toFixed(1) + '万';
    }
    return views.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${video.slug}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="relative w-40 h-24 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            title={video.title}
          />
          <span className="absolute bottom-1 right-1 bg-black/70 text-background-50 text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
            {video.duration}
          </span>
        </div>
        <div className="flex-1 min-w-0 py-1">
          <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {video.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{formatViews(video.views)}次播放</span>
            <span className="whitespace-nowrap flex items-center gap-1">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {video.rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${video.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={video.title}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded whitespace-nowrap">
          {video.duration}
        </span>
        <div className="absolute top-2 left-2">
          <span className="bg-primary-500/90 text-background-50 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
            {video.category}
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 flex items-center justify-center bg-primary-500/90 rounded-full">
            <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
          {video.title}
        </h3>
        <p className="text-xs md:text-sm text-foreground-600 mt-1.5 line-clamp-1">
          {video.description}
        </p>
        <div className="flex items-center justify-between mt-3 text-xs text-foreground-500">
          <span className="whitespace-nowrap">{formatViews(video.views)}次播放</span>
          <span className="whitespace-nowrap flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {video.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}